import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getInvoices,
  addPaymentToInvoice,
  getBcvRate,
  getPaymentMethods,
  getHistoricalRates,
} from "../utils/api";
import { Card, Button, Input } from "../components/legacy-ui";
import { AppPageHeader } from "../components/app-ui";
import { formatCurrency, sellerLabel } from "../utils/formatters";
import { useAuth } from "../context/AuthContext";
import { Search } from "lucide-react";
import InvoiceViewModal from "../components/InvoiceViewModal.js";
import { filterPaymentMethodsByCurrency } from "../utils/paymentUtils";
import { Helmet } from "react-helmet";
import { companyPageTitle } from "../utils/company.js";
import toast from "react-hot-toast";
import { LoadingState } from "../components/motion.js";

const formatAmountForDisplay = (value) => {
  if (!value) return "";
  const parts = value.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
};

const PaymentsModule = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentView, setShowPaymentView] = useState(false);
  const [invoiceToView, setInvoiceToView] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  const [amount, setAmount] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [useBcv, setUseBcv] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [reference, setReference] = useState("");
  const [filteredPaymentMethodsModule, setFilteredPaymentMethodsModule] = useState([]);

  const [currentBcvRate, setCurrentBcvRate] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const [historicalRates, setHistoricalRates] = useState([]);
  const [paidAt, setPaidAt] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [rateDate, setRateDate] = useState("");

  const [loading, setLoading] = useState(true);

  const parseAmountInput = (value) => {
    let raw = value.replace(/,/g, "");
    if (/^\d{1,3}(\.\d{3})+$/.test(raw)) raw = raw.replace(/\./g, "");
    if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) return raw;
    return null;
  };

  const handleAmountChange = (e) => {
    const parsed = parseAmountInput(e.target.value);
    if (parsed !== null) setAmount(parsed);
  };

  const handleAmountUsdChange = (e) => {
    const parsed = parseAmountInput(e.target.value);
    if (parsed !== null) setAmountUsd(parsed);
  };

  const formatRate = (rate) => {
    if (!rate && rate !== 0) return "";
    return Number(rate).toLocaleString("es-VE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchPendingInvoices = useCallback(async (searchQuery, sortOrder) => {
    setLoading(true);
    try {
      const params = { status: "pending", sortBy: sortOrder };
      if (searchQuery) params.search = searchQuery;
      const invoicesRes = await getInvoices(params);
      setInvoices(invoicesRes.data.data);
    } catch (error) {
      console.error("Error fetching pending invoices:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchPendingInvoices(debouncedSearchTerm, sortBy);
  }, [user, debouncedSearchTerm, sortBy, fetchPendingInvoices]);

  useEffect(() => {
    if (user) {
      const fetchStaticData = async () => {
        try {
          const [bcvRateRes, paymentMethodsRes, historicalRatesRes] = await Promise.all([getBcvRate(), getPaymentMethods(), getHistoricalRates()]);
          const currentRate = bcvRateRes.data.data.rate;
          setCurrentBcvRate(currentRate);
          setSelectedRate(currentRate);
          setPaymentMethods(paymentMethodsRes.data.data);
          if (paymentMethodsRes.data.data.length > 0) setPaymentMethod(paymentMethodsRes.data.data[0]._id);
          const uniqueRates = historicalRatesRes.data.usd.reduce((acc, current) => {
            if (!acc.find((item) => item.last_update === current.last_update && item.price === current.price)) acc.push(current);
            return acc;
          }, []);
          setHistoricalRates(uniqueRates.sort((a, b) => new Date(b.last_update) - new Date(a.last_update)));
        } catch (error) {
          console.error("Error fetching static data:", error);
        }
      };
      fetchStaticData();
    }
  }, [user]);

  const ratesByDate = useMemo(() => {
    if (!historicalRates || historicalRates.length === 0) return {};
    const rateMap = {};
    historicalRates.forEach((rate) => {
      const date = rate.last_update.slice(0, 10);
      if (!rateMap[date]) rateMap[date] = rate.price;
    });
    return rateMap;
  }, [historicalRates]);

  const availableDates = useMemo(() => {
    return Object.keys(ratesByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [ratesByDate]);

  useEffect(() => {
    if (availableDates.length > 0 && !rateDate) {
      setRateDate(paidAt && ratesByDate[paidAt] ? paidAt : availableDates[0]);
    }
  }, [availableDates, rateDate, paidAt, ratesByDate]);

  useEffect(() => {
    if (currency === "BS") {
      const rate = ratesByDate[rateDate];
      setSelectedRate(rate || "");
    } else {
      setSelectedRate(currentBcvRate);
    }
  }, [rateDate, ratesByDate, currency, currentBcvRate]);

  useEffect(() => {
    const methods = filterPaymentMethodsByCurrency(paymentMethods, currency);
    setFilteredPaymentMethodsModule(methods);
    if (!methods.some((pm) => pm._id === paymentMethod)) setPaymentMethod(methods.length > 0 ? methods[0]._id : "");
  }, [currency, paymentMethods, paymentMethod]);

  const resetForm = () => {
    setAmount("");
    setAmountUsd("");
    setCurrency("USD");
    setUseBcv(true);
    setReference("");
    setSelectedInvoice(null);
    setShowPaymentView(false);
    if (availableDates.length > 0) {
      setRateDate(paidAt && ratesByDate[paidAt] ? paidAt : availableDates[0]);
    }
  };

  const pendingAmount = Number(selectedInvoice?.pendingAmount) || 0;

  const suggestBsAmount = (rate) => {
    if (!pendingAmount || pendingAmount <= 0 || !rate) return "";
    return Number((pendingAmount * rate).toFixed(2)).toString();
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    if (newCurrency !== "BS") {
      setUseBcv(true);
      setAmountUsd("");
      return;
    }
    if (useBcv) {
      setAmount(suggestBsAmount(ratesByDate[rateDate] || selectedRate));
    } else {
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    }
  };

  const handleUseBcvChange = (checked) => {
    setUseBcv(checked);
    if (checked) {
      setAmount(suggestBsAmount(ratesByDate[rateDate] || selectedRate));
      setAmountUsd("");
    } else {
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    }
  };

  const handleRateDateChange = (newDate) => {
    setRateDate(newDate);
    if (currency === "BS" && useBcv) {
      const rate = ratesByDate[newDate];
      if (rate && pendingAmount > 0) {
        setAmount(Number((pendingAmount * rate).toFixed(2)).toString());
      }
    }
  };

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    if (isMobile) setShowPaymentView(true);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice || !amount) return;
    if (currency === "BS" && useBcv && !selectedRate) {
      toast.error("No hay tasa de cambio disponible para la fecha seleccionada.", {
        position: "top-center",
      });
      return;
    }
    const usdValue = parseFloat(amountUsd) || 0;
    if (currency === "BS" && !useBcv && !(usdValue > 0)) {
      toast.error("Indica el monto en dólares.", { position: "top-center" });
      return;
    }
    const paymentData = { amount: Number(amount), currency, paymentMethod, reference, paymentDate: paidAt };
    if (currency === "BS") {
      paymentData.exchangeRate = useBcv ? selectedRate : Number(amount) / usdValue;
    }
    try {
      await addPaymentToInvoice(selectedInvoice._id, paymentData);
      toast.success("Pago registrado con éxito", { position: "top-center" });
      resetForm();
      fetchPendingInvoices(debouncedSearchTerm, sortBy);
    } catch (err) {
      console.error(err);
      toast.error(`Error al registrar el pago: ${err.response?.data?.error || err.message}`, {
        position: "top-center",
      });
    }
  };

  const openInvoiceModal = (invoice, event) => {
    if (event) event.stopPropagation();
    setInvoiceToView(invoice);
    setIsInvoiceModalOpen(true);
  };

  const closeInvoiceModal = () => {
    setIsInvoiceModalOpen(false);
    setInvoiceToView(null);
  };

  const numericAmount = parseFloat(amount) || 0;
  const numericAmountUsd = parseFloat(amountUsd) || 0;
  const rateNum = parseFloat(selectedRate) || 0;
  const manualRate =
    currency === "BS" && !useBcv && numericAmount > 0 && numericAmountUsd > 0
      ? numericAmount / numericAmountUsd
      : 0;
  const amountInUSD =
    currency === "USD"
      ? numericAmount || null
      : currency === "BS" && !useBcv
        ? numericAmountUsd || null
        : currency === "BS" && rateNum > 0 && numericAmount
          ? numericAmount / rateNum
          : null;
  let remainingAmount;
  if (selectedInvoice && numericAmount > 0 && amountInUSD !== null) {
    remainingAmount = selectedInvoice.pendingAmount - amountInUSD;
  }

  const PaymentForm = (
    <form onSubmit={handlePayment} className="space-y-4">
      <div className="bg-gray-100 p-3 rounded-lg text-sm mb-4">
        Factura: <strong>{selectedInvoice ? `#${selectedInvoice.invoiceNumber} - ${selectedInvoice.client.name}` : "Ninguna seleccionada"}</strong>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Moneda</label>
          <select className="w-full p-3 border border-gray-300 rounded-lg bg-white" value={currency} onChange={(e) => handleCurrencyChange(e.target.value)}>
            <option value="USD">Dólares ($)</option>
            <option value="BS">Bolívares (Bs)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Fecha del pago</label>
          <input
            type="date"
            required
            className="w-full p-3 border border-gray-300 rounded-lg bg-white"
            value={paidAt}
            onChange={(e) => {
              const next = e.target.value;
              setPaidAt(next);
              if (currency === "BS" && useBcv && ratesByDate[next]) handleRateDateChange(next);
            }}
          />
        </div>
      </div>

      {currency === "BS" && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={useBcv}
            onChange={(e) => handleUseBcvChange(e.target.checked)}
          />
          Usar tasa BCV
        </label>
      )}

      {currency === "BS" && useBcv && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fecha de la Tasa</label>
            <input
              type="date"
              className="w-full p-2 border rounded-lg bg-white"
              value={rateDate}
              onChange={(e) => handleRateDateChange(e.target.value)}
              min={availableDates.length > 0 ? availableDates[availableDates.length - 1] : ""}
              max={availableDates.length > 0 ? availableDates[0] : ""}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tasa de Cambio</label>
            <input type="text" className="w-full p-2 border rounded-lg bg-gray-100" value={selectedRate ? formatRate(selectedRate) : ""} readOnly placeholder="N/A" />
          </div>
        </div>
      )}

      <Input label={`Monto en ${currency === "BS" ? "Bs" : "USD"}`} type="text" inputMode="decimal" placeholder="0.00" required value={formatAmountForDisplay(amount)} onChange={handleAmountChange} />
      {currency === "BS" && useBcv && amountInUSD !== null && numericAmount > 0 && (
        <p className="text-xs text-gray-500 mt-1">Equivalente a: {formatCurrency(amountInUSD, "USD")}</p>
      )}
      {numericAmount > 0 && remainingAmount !== undefined && (
        <p className="text-sm text-gray-500 mt-1">Pendiente restante: <strong className="font-mono">{formatCurrency(remainingAmount)}</strong></p>
      )}

      {currency === "BS" && !useBcv && (
        <div className="space-y-3">
          <Input label="Monto $" type="text" inputMode="decimal" placeholder="0.00" required value={formatAmountForDisplay(amountUsd)} onChange={handleAmountUsdChange} />
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tasa usada (Bs/$)</label>
            <input
              type="text"
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 font-semibold text-gray-800"
              value={manualRate > 0 ? formatRate(manualRate) : ""}
              placeholder="Se calcula con Bs y $"
            />
          </div>
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Método de Pago</label>
        <select className="w-full p-3 border border-gray-300 rounded-lg bg-white" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          <option value="" disabled>Seleccione un método</option>
          {filteredPaymentMethodsModule.map((method) => (<option key={method._id} value={method._id}>{method.name}</option>))}
        </select>
      </div>
      <Input label="Referencia" type="text" placeholder="Nro. de confirmación, etc." value={reference} onChange={(e) => setReference(e.target.value)} />
      <Button type="submit" className="w-full mt-4" disabled={!selectedInvoice || !amount || (currency === "BS" && useBcv && !selectedRate) || (currency === "BS" && !useBcv && !amountUsd)}>
        Procesar Pago
      </Button>
    </form>
  );

  if (isMobile && showPaymentView) {
    return (
      <div className="space-y-6 p-6 h-full overflow-y-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="secondary" onClick={() => { setShowPaymentView(false); setSelectedInvoice(null); }}>&#8592; Volver</Button>
          <h2 className="text-xl font-bold text-gray-900 my-6">Registrar pago</h2>
        </div>
        <Card className="p-4">{PaymentForm}</Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Pagos")}</title>
      </Helmet>
      <div className="p-3 sm:p-6 max-w-full overflow-x-hidden">
        <div className="mb-4 sm:mb-6">
          <AppPageHeader
            title="Reportes de pago"
            subtitle="Procesar pagos para facturas pendientes"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:h-[calc(100vh-160px)]">
          <Card className="p-4 flex flex-col">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Facturas Pendientes</h3>
            <div className="flex gap-2 mb-4 flex-shrink-0">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type="text" placeholder="Buscar..." className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <select className="border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date-desc">Más Recientes</option>
                <option value="date-asc">Más Antiguas</option>
                <option value="debt-desc">Mayor Deuda</option>
                <option value="debt-asc">Menor Deuda</option>
                <option value="invoiceNumber-asc">Nro. Factura (Asc)</option>
                <option value="invoiceNumber-desc">Nro. Factura (Desc)</option>
              </select>
            </div>
            <div className="reveal-host space-y-2" style={{ overflowY: "auto", flexGrow: 1, maxHeight: "400px" }}>
              {loading ? <LoadingState count={4} label="Buscando facturas" /> : invoices.length > 0 ? (
                invoices.map((inv) => (
                  <div key={inv._id} onClick={() => handleInvoiceClick(inv)} className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedInvoice?._id === inv._id && !isMobile ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>
                    <div className="flex justify-between">
                      <span className="text-sm font-semibold text-gray-900">{inv.client.name}</span>
                      <span className="text-red-600 font-semibold text-sm">{formatCurrency(inv.pendingAmount)}</span>
                    </div>
                    {sellerLabel(inv.client?.seller) ? (
                      <span className="inline-flex mt-1 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
                        {sellerLabel(inv.client?.seller)}
                      </span>
                    ) : null}
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span># {inv.invoiceNumber}</span>
                      <span>{inv.date ? new Date(inv.date).toLocaleDateString("es-VE", { year: "numeric", month: "2-digit", day: "2-digit" }) : null}</span>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-xs !px-2 !py-1"
                        onClick={(e) => openInvoiceModal(inv, e)}
                      >
                        Ver factura
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No se encontraron facturas pendientes.</p>
              )}
            </div>
          </Card>
          {!isMobile && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">Detalles del Pago</h3>
              {PaymentForm}
            </Card>
          )}
        </div>
      </div>

      <InvoiceViewModal
        open={isInvoiceModalOpen}
        onClose={closeInvoiceModal}
        invoice={invoiceToView}
        titlePrefix="Factura"
        mode="readonly"
        paymentMethods={paymentMethods}
      />
    </>
  );
};

export default PaymentsModule;